import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

export interface OrchestrationState {
  id: string;
  current_phase: 'content' | 'quizzes' | 'practice' | 'assessments' | 'complete';
  phase1_content_complete: boolean;
  phase2_quizzes_complete: boolean;
  phase3_practice_complete: boolean;
  phase4_assessments_complete: boolean;
  auto_generation_complete: boolean;
  started_at: string;
  completed_at: string | null;
  last_phase_completed_at: string | null;
  metadata: any;
}

/**
 * Orchestration Service
 * Manages the master content orchestration process
 */
export class OrchestrationService {
  private static instance: OrchestrationService;

  static getInstance(): OrchestrationService {
    if (!OrchestrationService.instance) {
      OrchestrationService.instance = new OrchestrationService();
    }
    return OrchestrationService.instance;
  }

  /**
   * Get current orchestration state
   */
  async getState(): Promise<OrchestrationState | null> {
    try {
      const { data, error } = await supabase
        .from('orchestration_state')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) throw error;
      return data as any as OrchestrationState;
    } catch (error) {
      logger.error('Failed to get orchestration state:', error);
      return null;
    }
  }

  /**
   * Trigger master orchestrator
   */
  async triggerOrchestrator(): Promise<{ success: boolean; message: string }> {
    try {
      logger.info('🎯 Triggering master content orchestrator...');

      const { data, error } = await supabase.functions.invoke('master-content-orchestrator');

      if (error) throw error;

      logger.info('✅ Orchestrator triggered:', data);
      return { success: true, message: data?.message || 'Orchestrator running' };
    } catch (error: any) {
      logger.error('Failed to trigger orchestrator:', error);
      return { success: false, message: error.message };
    }
  }

  /**
   * Check if orchestration is complete
   */
  async isComplete(): Promise<boolean> {
    const state = await this.getState();
    return state?.auto_generation_complete || false;
  }

  /**
   * Get orchestration progress percentage
   */
  async getProgress(): Promise<number> {
    const state = await this.getState();
    if (!state) return 0;

    let completedPhases = 0;
    if (state.phase1_content_complete) completedPhases++;
    if (state.phase2_quizzes_complete) completedPhases++;
    if (state.phase3_practice_complete) completedPhases++;
    if (state.phase4_assessments_complete) completedPhases++;

    return Math.round((completedPhases / 4) * 100);
  }


  /**
   * Subscribe to orchestration state changes
   */
  subscribeToState(callback: (state: OrchestrationState) => void): () => void {
    const subscription = supabase
      .channel('orchestration_state_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orchestration_state'
        },
        (payload) => {
          logger.info('Orchestration state updated:', payload.new);
          callback(payload.new as OrchestrationState);
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }
}

export const orchestrationService = OrchestrationService.getInstance();
